import angular from "angular";
import { Configs } from "@salaxy/core";

/**
 * Controller for code editing user interfaces: JSON Schema, JSON, JavaScript and HTML.
 */
export class SxyCodeEditorController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$transclude", "$element", "$sce"];

  /** Type of user interface to render: code-editor or basic HTML (textarea/pre) */
  public mode: "monaco" | "basic";

  /** If true, the component is read-only */
  public readOnly: boolean;

  /**
   * Type of the main object that is called "item" in the code.
   * Type should be one of @salaxy/core types (e.g. "Calculation") or supported
   * native types (currently ["any", "string", "[]"]). Default is any.
   */
  public itemType: string;

  /**
   * Language of the code, e.g. "javascript" (default), "html", "json" or "css".
   * Currently only affects the externalcodeeditor.
   */
  public lang: string;

  /** The model that is bound to the input */
  public model: angular.INgModelController;

  /** The height of the editor as CSS, (e.g. "calc(100vh - 600px)"). Default is "400px" */
  public height: string;

  /** Type of the editor to use: "monaco" (default), "sample" or "formula". */
  public editor: "monaco" | "sample" | "formula";

  /**
   * Raw value field, mainly used in direct AngularJS NgModel data binding and as internal storage.
   * Typically, you should use get/set code for display and setting of the value.
   */
  public value: string;

  /** salaxy code-editor URL */
  public codeEditorUrl: string;

  private targetOrigin: string;

  /**
   * IFrame element that hosts the external code editor.
   * Only set after the window has loaded and sent the initialization message (external code editor is ready).
   */
  public codeEditorIFrame: HTMLIFrameElement;

  constructor(

    private $transclude: angular.ITranscludeFunction,

    private $element: JQLite,
    private $sce: angular.ISCEService
  ) {

  }

  /** Initializes the controller */
  public $onInit() {
    if (!this.editor) {
      this.editor = "monaco";
    }
    this.registerTransclude();
    this.targetOrigin = Configs.current.codeEditor;
    if (this.model) {
      this.model.$render = () => {
        this.value = this.model.$viewValue;
        if (this.codeEditorIFrame) {
          this.codeEditorIFrame.contentWindow.postMessage({ code: this.code }, this.codeEditorIFrame.contentWindow.origin);
        }
      };
    }
    this.registerCodeEditorWindow();
  }

  /** Clears the window event hooks */
  public $onDestroy() {
    window.removeEventListener("message", this.onEditorMessage, false);
  }

  /** The code that is shown in editor. */
  public get code(): string {
    return this.value;
  }
  public set code(value: string) {
    this.value = value;
    if (this.model) {
      this.model.$setViewValue(this.value);
    }
  }

  /** On change of the value, do preventive operations and set value to model. */
  protected onChange() {
    if (this.model) {
      this.model.$setViewValue(this.value);
    }
  }

  private registerCodeEditorWindow() {
    if (this.mode == "basic") {
      return;
    }
    if(!this.targetOrigin) {
      return;
    }
    this.codeEditorUrl = this.$sce.trustAsResourceUrl(`${this.targetOrigin}?editor=${this.editor}&readonly=${this.readOnly}&lang=${ this.lang || ""}`);
    if (this.itemType) {
      this.codeEditorUrl = this.$sce.trustAsResourceUrl(`${this.codeEditorUrl}&item=${this.itemType}`);
    }
    window.addEventListener("message", this.onEditorMessage, false);
  }

  private onEditorMessage = (ev: MessageEvent<any>) => {
    if (ev.origin !== this.targetOrigin) {
      return;
    }
    if (this.codeEditorIFrame) {
      if (this.codeEditorIFrame.contentWindow !== ev.source) {
        return;
      }
      if (Object.prototype.hasOwnProperty.call(ev.data, "code")) {
        this.code = ev.data.code;
      }
    }
    else if (Object.prototype.hasOwnProperty.call(ev.data, "loaded")) {
      // First message is for registration only.
      const iFrame = this.codeEditorIFrame = (this.$element.find("iframe")[0] as HTMLIFrameElement);
      if (iFrame.contentWindow !== ev.source) {
        return;
      }
      this.codeEditorIFrame = iFrame;
      iFrame.contentWindow.postMessage({ code: this.code }, this.targetOrigin);
      return;
    }
  };

  private registerTransclude() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.$transclude((clonedElement, scope) => {
      let content = "";
      for (let i = 0; i < clonedElement.length; i++) {
        if (clonedElement[i].nodeType === 3) {
          content += (clonedElement[i] as unknown as Text).textContent;
        } else {
          content += (clonedElement[i].outerHTML || "").replace(" ng-scope", "").replace("ng-scope", "").replace(" class=\"\"", "");
        }
      }
      content = content.trim();
      if (content) {
        this.value = content;
      }
      return clonedElement;
    });
  }
}